/**
 * The generic presets that carry a task: a thermal camera and a plate reader.
 * One definition for the catalogue list (cameraManufacturers.ts, the lazy
 * client) and the quick-pick tiles, so a tile and the list place the same
 * camera. Each figure is derived from the catalogue or from a published
 * criterion, named beside it.
 */
import type { CameraModel } from '@/components/cctv-designer-v2/types';
import type { ThermalSpec } from '@/lib/thermal/types';
import { calculateThermalRange } from '@/lib/thermal/johnsonCriteria';
import { EU_PLATE_WIDTH_M, PLATE_SOFTWARE_PIXELS } from '@/components/cctv-designer-v2/lighting/plateOptics';

const angleDeg = (halfWidthMm: number, focalMm: number) =>
  Math.round((2 * Math.atan(halfWidthMm / focalMm) * 1800) / Math.PI) / 10;

// Modes of the 41 datasheet-verified pure-thermal models in thermalSpecsIndex
// (384x288, 17 um, VOx, 50 mK, bullet); 15 mm is the lens nearest their median HFOV of 24 deg.
const THERMAL_SPEC: ThermalSpec = {
  resolution: '384x288',
  pixelPitch_um: 17,
  sensorType: 'VOx',
  netd_mk: 50,
  spectralRange_um: '8-14',
  focalLength_mm: 15,
  hfov_deg: angleDeg((384 * 0.017) / 2, 15),
  vfov_deg: angleDeg((288 * 0.017) / 2, 15),
};

export const GENERIC_THERMAL: CameraModel = {
  name: 'Generic Thermal',
  type: 'bullet',
  hfov: THERMAL_SPEC.hfov_deg,
  // Person detection by the app's own Johnson path, as for any thermal model without a DRI table.
  range: Math.round(calculateThermalRange(THERMAL_SPEC, 'detect', 'person') ?? 0),
  irRange: 0,
  resolution: THERMAL_SPEC.resolution,
  focalLength: '15mm',
  sensorClass: 'thermal',
  thermalSpec: THERMAL_SPEC,
};

// lprIndex models: 2688x1520 is their modal resolution, 42.5 deg the median wide end
// of their 7-22/8-32 mm variants (8-32 mm in six of eight), 50 m the median IR range they state.
const LPR_PIXELS = 2688;
const LPR_HFOV_DEG = 42.5;

export const GENERIC_LPR: CameraModel = {
  name: 'Generic LPR',
  type: 'bullet',
  hfov: LPR_HFOV_DEG,
  // Farthest whole metre at which an EU plate still spans the software minimum (Axis, plateOptics).
  range: Math.floor(
    (EU_PLATE_WIDTH_M * LPR_PIXELS) / PLATE_SOFTWARE_PIXELS.min / (2 * Math.tan((LPR_HFOV_DEG * Math.PI) / 360)),
  ),
  irRange: 50,
  resolution: '2688x1520',
  focalLength: '8-32mm',
};
